import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Send, Loader2, AlertCircle, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Format relative time
const formatRelativeTime = (timestamp) => {
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins} min ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  if (diffDays === 1) return `Yesterday ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
  if (diffDays < 7) return `${diffDays} days ago`;
  return date.toLocaleDateString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

// Chat Header Component
const ChatHeader = ({ professionalName, clientName }) => (
  <header className="bg-white border-b border-slate-200 px-4 py-4 sticky top-0 z-10">
    <div className="max-w-2xl mx-auto">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-[#5856D6] rounded-full flex items-center justify-center">
          <span className="text-white font-semibold text-sm">
            {professionalName?.split(' ').map(n => n[0]).join('').slice(0, 2)}
          </span>
        </div>
        <div>
          <h1 className="font-semibold text-slate-900" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            Chat with {professionalName}
          </h1>
          <p className="text-sm text-slate-500">You: {clientName}</p>
        </div>
      </div>
    </div>
  </header>
);

// Individual Message Component
const ChatMessage = ({ message, isClient }) => (
  <div className={`flex ${isClient ? 'justify-end' : 'justify-start'} mb-3`}>
    <div className={`max-w-[75%] ${isClient ? 'order-2' : 'order-1'}`}>
      <div
        className={`px-4 py-2.5 rounded-2xl ${
          isClient
            ? 'bg-[#5856D6] text-white rounded-br-md'
            : 'bg-white text-slate-900 border border-slate-200 rounded-bl-md'
        }`}
        style={{ wordBreak: 'break-word' }}
      >
        <p className="text-[15px] leading-relaxed">{message.content}</p>
      </div>
      <p className={`text-xs text-slate-400 mt-1 ${isClient ? 'text-right' : 'text-left'}`}>
        {formatRelativeTime(message.timestamp)}
      </p>
    </div>
  </div>
);

// Chat Input Component
const ChatInput = ({ onSend, disabled, isSending }) => {
  const [text, setText] = useState("");

  const handleSubmit = (e) => {
    e.preventDefault();
    if (text.trim() && !disabled && !isSending) {
      onSend(text.trim());
      setText("");
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  return (
    <div className="bg-white border-t border-slate-200 px-4 py-3 sticky bottom-0">
      <form onSubmit={handleSubmit} className="max-w-2xl mx-auto">
        <div className="flex items-end gap-2">
          <div className="flex-1 relative">
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type a message..."
              disabled={disabled}
              rows={1}
              className="w-full px-4 py-3 bg-slate-100 rounded-2xl resize-none focus:outline-none focus:ring-2 focus:ring-[#5856D6]/20 focus:bg-white border border-transparent focus:border-[#5856D6]/30 text-slate-900 placeholder-slate-400 disabled:opacity-50"
              style={{ minHeight: '48px', maxHeight: '120px' }}
            />
          </div>
          <Button
            type="submit"
            disabled={!text.trim() || disabled || isSending}
            className="h-12 w-12 rounded-full bg-[#5856D6] hover:bg-[#4745B5] disabled:opacity-50 p-0 flex items-center justify-center"
          >
            {isSending ? (
              <Loader2 className="w-5 h-5 animate-spin text-white" />
            ) : (
              <Send className="w-5 h-5 text-white" />
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

// Loading State Component
const LoadingState = () => (
  <div className="flex-1 flex items-center justify-center">
    <div className="text-center">
      <Loader2 className="w-8 h-8 animate-spin text-[#5856D6] mx-auto mb-3" />
      <p className="text-slate-500">Loading chat...</p>
    </div>
  </div>
);

// Error State Component
const ErrorState = ({ message }) => (
  <div className="flex-1 flex items-center justify-center px-4">
    <div className="text-center max-w-sm">
      <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <AlertCircle className="w-8 h-8 text-red-500" />
      </div>
      <h2 className="text-xl font-semibold text-slate-900 mb-2" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
        Chat Not Found
      </h2>
      <p className="text-slate-500">{message}</p>
    </div>
  </div>
);

// Empty Messages Component
const EmptyMessages = ({ professionalName }) => (
  <div className="flex-1 flex items-center justify-center px-4">
    <div className="text-center max-w-sm">
      <div className="w-16 h-16 bg-[#5856D6]/10 rounded-full flex items-center justify-center mx-auto mb-4">
        <MessageSquare className="w-8 h-8 text-[#5856D6]" />
      </div>
      <h2 className="text-xl font-semibold text-slate-900 mb-2" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
        No messages yet
      </h2>
      <p className="text-slate-500">Start the conversation with {professionalName}!</p>
    </div>
  </div>
);

// Main Client Chat Page
const ClientChat = () => {
  const { chatId } = useParams();
  const [chatData, setChatData] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSending, setIsSending] = useState(false);
  const messagesEndRef = useRef(null);
  const pollIntervalRef = useRef(null);

  // Scroll to bottom
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Fetch chat data
  const fetchChatData = async () => {
    try {
      const response = await axios.get(`${API}/chat/${chatId}`);
      setChatData(response.data);
      return response.data;
    } catch (err) {
      if (err.response?.status === 404) {
        setError("This chat link is invalid or has expired.");
      } else {
        setError("Failed to load chat. Please try again.");
      }
      throw err;
    }
  };

  // Fetch messages
  const fetchMessages = async (conversationId) => {
    try {
      const response = await axios.get(`${API}/messages/${conversationId}`);
      setMessages(response.data);
    } catch (err) {
      console.error("Error fetching messages:", err);
    }
  };

  // Initial load
  useEffect(() => {
    const loadChat = async () => {
      setLoading(true);
      try {
        const data = await fetchChatData();
        await fetchMessages(data.conversation_id);
      } catch (err) {
        // Error already handled
      } finally {
        setLoading(false);
      }
    };

    loadChat();

    // Cleanup
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [chatId]);

  // Poll for new messages
  useEffect(() => {
    if (chatData?.conversation_id) {
      pollIntervalRef.current = setInterval(() => {
        fetchMessages(chatData.conversation_id);
      }, 5000);
    }

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [chatData?.conversation_id]);

  // Scroll on new messages
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Send message
  const handleSendMessage = async (content) => {
    setIsSending(true);
    
    // Optimistic update
    const tempMessage = {
      id: 'temp-' + Date.now(),
      sender_type: 'client',
      content,
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, tempMessage]);
    scrollToBottom();

    try {
      const response = await axios.post(`${API}/chat/${chatId}/message`, { content });
      
      // Replace temp message with real one
      setMessages(prev => 
        prev.map(msg => 
          msg.id === tempMessage.id 
            ? { ...response.data, sender_type: 'client' }
            : msg
        )
      );
    } catch (err) {
      // Remove failed message
      setMessages(prev => prev.filter(msg => msg.id !== tempMessage.id));
      alert("Failed to send message. Please try again.");
    } finally {
      setIsSending(false);
    }
  };

  // Render loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-[#F2F2F7] flex flex-col">
        <LoadingState />
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className="min-h-screen bg-[#F2F2F7] flex flex-col">
        <ErrorState message={error} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F2F2F7] flex flex-col">
      <ChatHeader 
        professionalName={chatData?.professional_name} 
        clientName={chatData?.client_name} 
      />
      
      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto px-4 py-4">
        <div className="max-w-2xl mx-auto">
          {messages.length === 0 ? (
            <EmptyMessages professionalName={chatData?.professional_name} />
          ) : (
            <>
              {messages.map((message) => (
                <ChatMessage
                  key={message.id}
                  message={message}
                  isClient={message.sender_type === 'client'}
                />
              ))}
              <div ref={messagesEndRef} />
            </>
          )}
        </div>
      </div>

      <ChatInput 
        onSend={handleSendMessage} 
        disabled={!!error} 
        isSending={isSending}
      />
    </div>
  );
};

export default ClientChat;
